import { useEffect, useMemo, useState } from 'react';
import { BarChart2, Check, Lightbulb } from 'lucide-react';
import Tooltip from '../Tooltip.jsx';
import ChatInput from '../ChatInput/ChatInput.jsx';
import ChatPanelSkeleton from './ChatPanelSkeleton.jsx';
import ChatPanelAgentViewSkeleton from './ChatPanelAgentViewSkeleton.jsx';
import { getAgentInitials } from './chatPanelViewUtils.js';

const DEFAULT_AGENT_NAME = 'Assistant';
const DEFAULT_MODEL = '—';
const DEFAULT_DESCRIPTION =
  'Ask something to get started. I can help you work through questions and tasks.';
const DEFAULT_STARTERS_COLLAPSED_COUNT = 3;
const DEFAULT_MORE_PROMPTS_LABEL = 'More prompts';
const DEFAULT_FEWER_PROMPTS_LABEL = 'Show less';

/**
 * Agent-focused chat panel — hero + conversation starter cards + composer (Personal Assistant layout).
 * @param {object} props
 * @param {string} [props.agentName]
 * @param {string} [props.modelLabel] — "Model used by the creator" line
 * @param {string} [props.description]
 * @param {boolean} [props.isLoadingAgentDetails]
 * @param {boolean} [props.isLoadingMessages] — shows full-panel skeleton overlay
 * @param {string[]} [props.starters]
 * @param {(text: string) => void} [props.onStarterClick]
 * @param {number} [props.initialVisibleStarters=3] — collapsed list size before "More prompts"
 * @param {string} [props.morePromptsLabel]
 * @param {string} [props.fewerPromptsLabel]
 * @param {object} props.chatInputProps — forwarded to {@link ChatInput}
 */
const ChatPanelAgentView = ({
  agentName = DEFAULT_AGENT_NAME,
  modelLabel = DEFAULT_MODEL,
  description = DEFAULT_DESCRIPTION,
  isLoadingAgentDetails = false,
  isLoadingMessages = false,
  starters = [],
  onStarterClick,
  initialVisibleStarters = DEFAULT_STARTERS_COLLAPSED_COUNT,
  morePromptsLabel = DEFAULT_MORE_PROMPTS_LABEL,
  fewerPromptsLabel = DEFAULT_FEWER_PROMPTS_LABEL,
  aboveComposerSlot = null,
  chatInputProps,
}) => {
  const { composerMountKey = 0, ...chatInputRest } = chatInputProps ?? {};
  const initials = getAgentInitials(agentName);
  const [startersExpanded, setStartersExpanded] = useState(false);
  const startersSignature = useMemo(() => starters.join('\u0001'), [starters]);

  useEffect(() => {
    setStartersExpanded(false);
  }, [startersSignature]);

  const visibleStarters = useMemo(() => {
    if (!starters.length) return [];
    if (startersExpanded || starters.length <= initialVisibleStarters) return starters;
    return starters.slice(0, initialVisibleStarters);
  }, [starters, startersExpanded, initialVisibleStarters]);

  const hasMoreStarters = starters.length > initialVisibleStarters;

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <ChatPanelSkeleton show={isLoadingMessages} />
      <div
        className={`flex min-h-0 flex-1 flex-col overflow-y-auto px-3 py-2 sm:px-4 ${isLoadingMessages ? 'pointer-events-none opacity-40' : ''}`}
      >
        {isLoadingAgentDetails ? (
          <div className="mx-auto my-auto w-full min-w-0">
            <ChatPanelAgentViewSkeleton />
          </div>
        ) : (
          <div className="mx-auto my-auto flex w-full min-w-0 flex-col items-center gap-5">
            <div className="flex w-full max-w-lg flex-col items-center gap-3 text-center">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-brand-100 text-lg font-semibold text-brand-800 dark:bg-brand-900/40 dark:text-brand-200">
                {initials}
              </div>
              <h2 className="text-lg font-semibold text-content">{agentName}</h2>
              <div className="flex max-w-md items-start justify-center gap-1.5 text-left text-sm text-content-secondary">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden />
                <span>
                  <span className="text-content-muted">Model used by the creator:</span>{' '}
                  {modelLabel}
                </span>
              </div>
              <p className="max-w-lg text-sm leading-relaxed text-content-secondary">{description}</p>
            </div>

            {starters.length > 0 && (
              <div className="flex w-full max-w-lg min-w-0 flex-col items-center gap-2">
                <ul className="flex w-full list-none flex-col gap-2 p-0">
                  {visibleStarters.map((question, index) => (
                    <li key={`starter-${index}`} className="w-full">
                      <Tooltip
                        content={question}
                        position="top"
                        align="center"
                        arrow={false}
                        triggerClassName="flex w-full min-w-0 cursor-default"
                      >
                        <button
                          type="button"
                          onClick={() => onStarterClick?.(question)}
                          className="flex w-full items-start gap-3 rounded-xl border border-edge bg-surface px-3.5 py-3 text-left text-sm text-content shadow-sm transition-colors hover:border-edge-strong hover:bg-surface-hover dark:shadow-none"
                        >
                          <Lightbulb
                            className="mt-0.5 h-5 w-5 shrink-0 text-brand-600 dark:text-brand-400"
                            aria-hidden
                          />
                          <span className="line-clamp-3 min-w-0 flex-1 leading-snug">{question}</span>
                        </button>
                      </Tooltip>
                    </li>
                  ))}
                </ul>
                {hasMoreStarters && (
                  <button
                    type="button"
                    className="flex items-center justify-center gap-2 self-center rounded-lg px-2 py-1.5 text-sm font-medium text-brand-600 transition-colors hover:bg-surface-hover hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300"
                    onClick={() => setStartersExpanded((v) => !v)}
                    aria-expanded={startersExpanded}
                  >
                    <BarChart2 className="h-4 w-4 shrink-0" aria-hidden />
                    {startersExpanded ? fewerPromptsLabel : morePromptsLabel}
                  </button>
                )}
              </div>
            )}

            <div className="w-full max-w-lg shrink-0">
              {aboveComposerSlot ? (
                <div className="scrollbar-aimsos-auto mb-2 max-h-[45vh] min-w-0 overflow-y-auto text-left">
                  {aboveComposerSlot}
                </div>
              ) : null}
              <ChatInput
                key={composerMountKey}
                className="w-full min-w-0"
                growDirection="up"
                {...chatInputRest}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatPanelAgentView;
